import React, { useRef, useMemo } from "react";
import { cn } from "@/lib/utils";

/**
 * Marquee primitive for 3D perspective testimonial wall.
 * Horizontal or vertical auto-scrolling. Supports pause on hover & reverse.
 *
 * CSS vars: --duration (default 40s), --gap (default 1rem)
 * Animations come from tailwind.config.js: animate-marquee / animate-marquee-vertical
 */
export function Marquee({
  className,
  reverse = false,
  pauseOnHover = false,
  children,
  vertical = false,
  repeat = 4,
  ariaLabel,
  ariaLive = "off",
  ariaRole = "marquee",
  ...props
}) {
  const marqueeRef = useRef(null);

  const tracks = useMemo(
    () => (
      <>
        {Array.from({ length: repeat }, (_, i) => (
          <div
            key={i}
            className={cn(
              !vertical ? "flex-row [gap:var(--gap)]" : "flex-col [gap:var(--gap)]",
              "flex shrink-0 justify-around",
              !vertical && "animate-marquee flex-row",
              vertical && "animate-marquee-vertical flex-col",
              pauseOnHover && "group-hover:[animation-play-state:paused]",
              reverse && "[animation-direction:reverse]"
            )}
          >
            {children}
          </div>
        ))}
      </>
    ),
    [repeat, children, vertical, pauseOnHover, reverse]
  );

  return (
    <div
      {...props}
      ref={marqueeRef}
      data-slot="marquee"
      className={cn(
        "group flex overflow-hidden p-2 [--duration:40s] [--gap:1rem] [gap:var(--gap)]",
        {
          "flex-row": !vertical,
          "flex-col": vertical,
        },
        className
      )}
      aria-label={ariaLabel}
      aria-live={ariaLive}
      role={ariaRole}
      tabIndex={0}
    >
      {tracks}
    </div>
  );
}

export default Marquee;
